import { Button, makeStyles, Text, tokens } from '@fluentui/react-components';
import { DocumentMultipleRegular, SearchRegular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalL}`,
    textAlign: 'center',
  },
  icon: {
    fontSize: '40px',
    lineHeight: '40px',
    color: tokens.colorNeutralForeground4,
    marginBottom: tokens.spacingVerticalS,
  },
  description: {
    color: tokens.colorNeutralForeground2,
    maxWidth: '42ch',
  },
  action: {
    marginTop: tokens.spacingVerticalM,
  },
});

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.icon} aria-hidden="true">
        {icon ?? <DocumentMultipleRegular />}
      </div>

      <Text weight="semibold" size={400} as="h2">
        {title}
      </Text>

      {description && (
        <Text className={styles.description} size={300}>
          {description}
        </Text>
      )}

      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
}

/**
 * Nothing has ever been shared with this user. Distinct from "your filters
 * matched nothing" — they are different situations and must not share copy.
 */
export function NoDocumentsState() {
  return (
    <EmptyState
      title="No documents yet"
      description="When your legal team shares a document with you, it will appear here."
    />
  );
}

export function NoResultsState({ onClear }: { onClear: () => void }) {
  return (
    <EmptyState
      icon={<SearchRegular />}
      title="No matching documents"
      description="No documents match your current search and filters."
      action={
        <Button appearance="secondary" onClick={onClear}>
          Clear filters
        </Button>
      }
    />
  );
}
